// src/pages/Auth/LoginPage.tsx - FIXED VERSION
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, Mail, Lock, LogIn } from 'lucide-react';
import { userActions } from '../../store/reducers/userReducers';
import { uiActions } from '../../store/reducers/uiReducers';
import { googleAuthService } from '../../services/googleAuth';
import unifiedAuth from '../../utils/unifiedAuth';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';
  const successMessage = location.state?.message;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const response = await unifiedAuth.auth.login(formData.email, formData.password);
      
      // Update Redux store
      dispatch(userActions.setUserInfo(response.user));
      
      // Show success toast
      dispatch(uiActions.addNotification({
        message: `Welcome back, ${response.user.firstName || response.user.email}!`,
        type: 'success'
      }));
      
      navigate(from, { replace: true });
    } catch (error: unknown) {
      console.error('Login error:', error);
      const err = error as Error & { fieldErrors?: { field: string; message: string }[] };
      if (err.fieldErrors && err.fieldErrors.length > 0) {
        const fieldMap: { [key: string]: string } = {};
        for (const fe of err.fieldErrors) {
          fieldMap[fe.field] = fe.message;
        }
        setErrors(fieldMap);
      } else {
        const message = err.message || 'Login failed. Please try again.';
        setErrors({ submit: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const googleBtnRef = useRef<HTMLDivElement>(null);

  // Process a Google credential once the user clicks the Google button
  const handleGoogleCredential = useCallback(async (googleUser: { email: string; google_id: string }) => {
    try {
      setIsLoading(true);
      setErrors({});

      const response = await unifiedAuth.auth.googleLogin({
        email: googleUser.email,
        google_id: googleUser.google_id,
      });

      dispatch(userActions.setUserInfo(response.user));
      dispatch(uiActions.addNotification({
        message: `Welcome back, ${response.user.firstName || response.user.email}!`,
        type: 'success',
      }));
      navigate(from, { replace: true });
    } catch (error: unknown) {
      console.error('Google sign-in error:', error);
      const err = error as Error;
      if (err.message && err.message.includes('not found')) {
        setErrors({ submit: 'No account found with this Google account. Please sign up first.' });
      } else {
        setErrors({ submit: err.message || 'Google sign-in was unsuccessful. Please try again or use email login.' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate, from]);

  // Render the official Google Sign-In button
  useEffect(() => {
    if (!googleBtnRef.current) return;
    let cancelled = false;

    googleAuthService.renderButton(googleBtnRef.current).then((user) => {
      if (!cancelled) handleGoogleCredential(user);
    }).catch((err) => {
      if (!cancelled && err.message !== 'Sign-in cancelled') {
        console.error('Google sign-in error:', err);
        setErrors({ submit: err.message || 'Google sign-in was unsuccessful. Please try again or use email login.' });
      }
    });

    return () => {
      cancelled = true;
      googleAuthService.cancel();
    };
  }, [handleGoogleCredential]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="text-3xl logo-text font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent text-glow-hover">
            _strymhub
          </Link>
          
          <div className="mt-8 flex justify-center">
            <div className="bg-orange-500/20 rounded-full p-3">
              <LogIn className="h-8 w-8 text-orange-400" />
            </div>
          </div>
          
          <h2 className="mt-6 text-3xl font-bold text-white">
            Welcome back
          </h2>
          <p className="mt-2 text-gray-400">
            Sign in to access your content
          </p>

          {/* Success Message */}
          {successMessage && (
            <div className="mt-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm">{successMessage}</p>
            </div>
          )}
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`
                    block w-full pl-10 pr-3 py-3 border rounded-lg bg-slate-800/50 text-white
                    placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500
                    ${errors.email ? 'border-red-500' : 'border-gray-600 focus:border-orange-500'}
                  `}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`
                    block w-full pl-10 pr-10 py-3 border rounded-lg bg-slate-800/50 text-white
                    placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500
                    ${errors.password ? 'border-red-500' : 'border-gray-600 focus:border-orange-500'}
                  `}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-300" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-400">{errors.password}</p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
              >
                Forgot your password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-pink-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-900 text-gray-400">Or continue with</span>
              </div>
            </div>

            {/* Google Sign In — rendered by Google Identity Services */}
            <div
              ref={googleBtnRef}
              className="w-full flex justify-center items-center min-h-[44px]"
            />

            {/* Sign Up Link */}
            <p className="text-center text-sm text-gray-400">
              Don't have an account?{' '}
              <Link to="/signup" className="font-medium text-orange-400 hover:text-orange-300 transition-colors">
                Sign up here
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;